/**
 * Procedural Milky Way (Sprint 2, S2-08; replaces the sky texture of plan decision D12): a faint band along the real
 * galactic plane, brighter towards the galactic centre in Sagittarius, with noise and a dark dust lane. No texture,
 * so nothing to download and no licence question. Drawn behind the catalogue stars in the star scene.
 */
import * as THREE from 'three';
import { eqjToEcliptic } from '../core/ephemeris';
import { vec3 } from '../core/vec3';
import { NOISE_GLSL } from './shaders';

const DEG = Math.PI / 180;

/** Unit vector (ecliptic J2000) from equatorial J2000 right ascension and declination in degrees. */
function eclipticDir(raDeg: number, decDeg: number): THREE.Vector3 {
  const ra = raDeg * DEG;
  const dec = decDeg * DEG;
  const v = eqjToEcliptic(vec3(), Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec));
  return new THREE.Vector3(v.x, v.y, v.z).normalize();
}

/** North galactic pole and galactic centre (IAU 1958 definition, J2000 coordinates). */
export const GALACTIC_POLE = eclipticDir(192.85948, 27.12825);
export const GALACTIC_CENTRE = eclipticDir(266.40499, -28.93617);

export function createMilkyWay(): THREE.Mesh {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uPole: { value: GALACTIC_POLE },
      uCentre: { value: GALACTIC_CENTRE },
      uBrightness: { value: 1 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uPole;
      uniform vec3 uCentre;
      uniform float uBrightness;
      varying vec3 vDir;
      ${NOISE_GLSL}
      void main() {
        vec3 d = normalize(vDir);
        float b = asin(clamp(dot(d, uPole), -1.0, 1.0));          // galactic latitude
        // direction within the galactic plane; undefined at the galactic poles, where normalize(0) would give NaN —
        // and a single NaN pixel spreads over the whole screen through the bloom mip chain
        vec3 inPlaneRaw = d - uPole * dot(d, uPole);
        float inPlaneLen = length(inPlaneRaw);
        vec3 inPlane = inPlaneLen > 1e-4 ? inPlaneRaw / inPlaneLen : uCentre;
        // clamped: rounding can push it a hair below 0, and pow() of a negative base is undefined (NaN)
        float towardsCentre = inPlaneLen > 1e-4 ? clamp(0.5 + 0.5 * dot(inPlane, uCentre), 0.0, 1.0) : 0.5;
        float width = mix(0.10, 0.22, towardsCentre * towardsCentre);
        float band = exp(-(b * b) / (width * width));
        float bulge = exp(-(b * b) / 0.05) * pow(towardsCentre, 6.0);
        // most of the sky is far from the band: skip the noise there
        if (band + bulge < 0.004) {
          gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
          return;
        }
        float clouds = zfFbm(d * 7.0);
        // (smoothstep needs edge0 < edge1, so the falling edge is written as 1 - smoothstep)
        float lane = 1.0 - smoothstep(0.0, 0.035, abs(b + 0.01 * sin(dot(inPlane, uCentre) * 9.0)));
        float dust = lane * smoothstep(0.35, 0.75, zfFbm(d * 11.0 + 3.0));
        float glow = (band * (0.35 + 0.65 * clouds) + 0.8 * bulge) * (1.0 - 0.75 * dust);
        vec3 tint = mix(vec3(0.62, 0.66, 0.78), vec3(0.95, 0.82, 0.62), towardsCentre);
        gl_FragColor = vec4(tint * glow * 0.07 * uBrightness, 1.0);
        #include <colorspace_fragment>
      }`,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
    transparent: true,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(500, 48, 24), material);
  mesh.name = 'milky-way';
  mesh.renderOrder = -1;
  mesh.frustumCulled = false;
  return mesh;
}
